import React, { useEffect, useState, useCallback } from "react";
import { Alert, Button, Container, Form, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import { localDateString, offsetDays, todayLocal } from "../utils/dateTz";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const RANGES = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
  { value: "all", label: "All Time" },
];

const statCard = (label, value, color) => (
  <div
    style={{
      flex: 1,
      background: "#fff",
      borderRadius: 12,
      padding: "20px 22px",
      border: "1px solid #e5e7eb",
      minWidth: 160,
    }}
  >
    <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: "1.5rem", fontWeight: 700, color }}>{value}</div>
  </div>
);

const fmt = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildDayKeys = (n) => {
  const days = [];
  for (let i = 0; i < n; i++) days.push(offsetDays(-i));
  return days;
};

const fmtDay = (key) => {
  const d = new Date(key + "T12:00:00");
  const today = todayLocal();
  const yesterday = offsetDays(-1);
  const label = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (key === today) return `${label}  (Today)`;
  if (key === yesterday) return `${label}  (Yesterday)`;
  return label;
};

export default function AtmLoad() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [quickRows, setQuickRows] = useState(() => {
    const rows = {};
    buildDayKeys(15).forEach((k) => {
      rows[k] = { amount: "", cashStart: "", cashEnd: "", note: "" };
    });
    return rows;
  });
  const savingRef = React.useRef({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}api/atm-load`, { params: { range } });
      if (!data.success) throw new Error(data.message);
      setRecords(data.records);
      setSummary(data.summary);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateRow = (dayKey, field, value) => {
    setQuickRows((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  const autoSave = useCallback(async (dayKey) => {
    const row = quickRows[dayKey];
    if (!row) return;
    const cs = parseFloat(row.cashStart) || 0;
    const ce = parseFloat(row.cashEnd) || 0;
    if (row.cashStart === "" && row.cashEnd === "" && row.note === "") return;
    if (savingRef.current[dayKey]) return; // prevent parallel saves
    savingRef.current[dayKey] = true;
    try {
      const { data } = await axios.post(`${API_URL}api/atm-load`, {
        cashStart: cs,
        cashEnd: ce,
        date: dayKey,
        note: row.note || "",
      });
      if (!data.success) throw new Error(data.message);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      savingRef.current[dayKey] = false;
    }
  }, [quickRows, fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this ATM load record?")) return;
    try {
      await axios.delete(`${API_URL}api/atm-load/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const dayMap = {};
  for (const r of records) {
    const key = localDateString(new Date(r.date));
    if (!dayMap[key]) dayMap[key] = [];
    dayMap[key].push(r);
  }

  const dayKeys = buildDayKeys(15);

  return (
    <Container className="mt-4 mb-5">
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      <h2 style={{ fontWeight: 700, marginBottom: 20 }}>🏧 ATM Cash Load</h2>

      {/* Summary cards */}
      <div className="d-flex gap-3 mb-4 flex-wrap">
        {statCard("Total ATM Loads", fmt(summary.total || 0), "#7c3aed")}
        {statCard("Load Count", summary.count || 0, "#6b7280")}
      </div>

      {/* Quick-entry grid */}
      <div className="card p-3 mb-4">
        <h6 className="mb-3" style={{ fontWeight: 600 }}>ATM Load — Last 15 Days</h6>
        <div className="table-responsive">
          <Table bordered size="sm" className="mb-0" style={{ fontSize: "0.88rem" }}>
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 140 }}>Day</th>
                <th style={{ textAlign: "center", color: "#6b7280" }}>Cash Start</th>
                <th style={{ textAlign: "center", color: "#6b7280" }}>Cash End</th>
                <th style={{ textAlign: "center", color: "#7c3aed" }}>🏧 Loaded</th>
                <th style={{ minWidth: 120 }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {dayKeys.map((dk) => {
                const row = quickRows[dk] || {};
                const savedRec = (dayMap[dk] || [])[0];
                const isToday = dk === todayLocal();

                // auto-calculated loaded amount
                const cs = parseFloat(row.cashStart) || 0;
                const ce = parseFloat(row.cashEnd) || 0;
                const liveLoaded = cs > 0 || ce > 0 ? Math.max(ce - cs, 0) : null;
                const savedLoaded = savedRec?.amount || 0;

                return (
                  <tr key={dk} style={isToday ? { background: "#f5f3ff" } : {}}>
                    <td style={{ fontWeight: isToday ? 700 : 500, verticalAlign: "middle" }}>
                      {fmtDay(dk)}
                    </td>
                    <td style={{ padding: "4px 6px" }}>
                      {savedRec?.cashStart > 0 && (
                        <div style={{ fontWeight: 600, color: "#6b7280", textAlign: "right", fontSize: "0.85rem", marginBottom: 2 }}>
                          {fmt(savedRec.cashStart)}
                        </div>
                      )}
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={row.cashStart || ""}
                        onChange={(e) => updateRow(dk, "cashStart", e.target.value)}
                        onBlur={() => autoSave(dk)}
                        style={{ textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "4px 6px" }}>
                      {savedRec?.cashEnd > 0 && (
                        <div style={{ fontWeight: 600, color: "#6b7280", textAlign: "right", fontSize: "0.85rem", marginBottom: 2 }}>
                          {fmt(savedRec.cashEnd)}
                        </div>
                      )}
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={row.cashEnd || ""}
                        onChange={(e) => updateRow(dk, "cashEnd", e.target.value)}
                        onBlur={() => autoSave(dk)}
                        style={{ textAlign: "right" }}
                      />
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "right", fontWeight: 700, color: "#7c3aed", fontSize: "0.92rem" }}>
                      {liveLoaded !== null ? fmt(liveLoaded) : savedLoaded > 0 ? fmt(savedLoaded) : "—"}
                    </td>
                    <td style={{ padding: "4px 6px" }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="note…"
                        value={row.note || ""}
                        onChange={(e) => updateRow(dk, "note", e.target.value)}
                        onBlur={() => autoSave(dk)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      {/* History */}
      <div className="d-flex gap-2 mb-3 align-items-center">
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>History:</span>
        {RANGES.map((r) => (
          <Button
            key={r.value}
            size="sm"
            variant={range === r.value ? "dark" : "outline-secondary"}
            onClick={() => setRange(r.value)}
          >
            {r.label}
          </Button>
        ))}
        {loading && <Spinner size="sm" animation="border" className="ms-2" />}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="card p-0">
        <Table striped bordered hover responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th style={{ textAlign: "right" }}>Cash Start</th>
              <th style={{ textAlign: "right" }}>Load $</th>
              <th style={{ textAlign: "right" }}>Cash End</th>
              <th>Note</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  No ATM load records found.
                </td>
              </tr>
            ) : (
              records.map((r) => {
                return (
                  <tr key={r._id}>
                    <td>{new Date(r.date).toLocaleDateString()}</td>
                    <td style={{ textAlign: "right", color: "#6b7280" }}>{fmt(r.cashStart || 0)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "#7c3aed" }}>{fmt(r.amount)}</td>
                    <td style={{ textAlign: "right", color: "#6b7280" }}>{fmt(r.cashEnd || 0)}</td>
                    <td className="text-muted">{r.note || "—"}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(r._id)}
                      >
                        🗑️
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
}
